import {
  bfsReachable,
  canJoinUnits,
  canLoadInto,
  canMoveAndAttackSameTurn,
  coordEq,
  coordKey,
  canUnitDamage,
  effectiveAttackKind,
  forecastDamage,
  inBounds,
  isUnitInAttackRange,
  manhattan,
  shortestPath,
  UNIT_DEFS,
} from "@dailywars/core/engine";
import type { Coord, GameState, Unit } from "@dailywars/core/engine";

import type { AttackOption, JoinOption, LoadOption } from "#/game/selection/types";

export function computeAttackOptions({
  state,
  unit,
  fromPos,
}: {
  state: GameState;
  unit: Unit;
  fromPos: Coord;
}): AttackOption[] {
  if (unit.hasAttacked) return [];
  const projected: GameState = coordEq(unit.pos, fromPos)
    ? state
    : {
        ...state,
        units: state.units.map((u) => (u.id === unit.id ? { ...u, pos: fromPos } : u)),
      };
  const options: AttackOption[] = [];
  for (const enemy of state.units) {
    if (enemy.owner === unit.owner) continue;
    if (!isUnitInAttackRange(unit, manhattan(fromPos, enemy.pos))) continue;
    if (!canUnitDamage({ unit, defenderKind: enemy.kind })) continue;
    options.push({
      targetId: enemy.id,
      forecast: forecastDamage(projected, { attackerId: unit.id, targetId: enemy.id }),
    });
  }
  return options;
}

export function computeJoinOptions({
  state,
  unit,
  fromPos,
}: {
  state: GameState;
  unit: Unit;
  fromPos: Coord;
}): JoinOption[] {
  if (unit.hasMoved) return [];
  const options: JoinOption[] = [];
  for (const ally of state.units) {
    if (!canJoinUnits(unit, ally)) continue;
    if (
      coordEq(fromPos, ally.pos) ||
      shortestPath({
        state: state,
        unit: unit,
        dest: ally.pos,
        options: { allowOccupiedDestination: true },
      })
    ) {
      options.push({ targetId: ally.id });
    }
  }
  return options;
}

export function computeLoadOptions({
  state,
  unit,
  fromPos,
}: {
  state: GameState;
  unit: Unit;
  fromPos: Coord;
}): LoadOption[] {
  if (unit.hasMoved || unit.hasAttacked) return [];
  const options: LoadOption[] = [];
  for (const transport of state.units) {
    if (transport.id === unit.id) continue;
    if (!canLoadInto({ state, passenger: unit, transport })) continue;
    if (
      coordEq(fromPos, transport.pos) ||
      shortestPath({
        state: state,
        unit: unit,
        dest: transport.pos,
        options: { allowOccupiedDestination: true },
      })
    ) {
      options.push({ targetId: transport.id });
    }
  }
  return options;
}

function computeAttackTileKeysFromPositions({
  state,
  kind,
  positions,
}: {
  state: GameState;
  kind: Unit["kind"];
  positions: Coord[];
}): string[] {
  const def = UNIT_DEFS[kind];
  const keys = new Set<string>();
  for (const pos of positions) {
    for (let dx = -def.maxRange; dx <= def.maxRange; dx++) {
      const yBound = def.maxRange - Math.abs(dx);
      for (let dy = -yBound; dy <= yBound; dy++) {
        const dist = Math.abs(dx) + Math.abs(dy);
        if (dist < def.minRange || dist > def.maxRange) continue;
        const c: Coord = { x: pos.x + dx, y: pos.y + dy };
        if (!inBounds(state, c)) continue;
        keys.add(coordKey(c));
      }
    }
  }
  return [...keys];
}

export function computeAttackTileKeysFromPos({
  state,
  kind,
  pos,
}: {
  state: GameState;
  kind: Unit["kind"];
  pos: Coord;
}): string[] {
  return computeAttackTileKeysFromPositions({
    state,
    kind,
    positions: [pos],
  });
}

export function computeEnemyAttackTileKeys(state: GameState, unit: Unit): string[] {
  const attackFrom: Coord[] = canMoveAndAttackSameTurn(unit.kind)
    ? bfsReachable(state, unit)
    : [unit.pos];
  return computeAttackTileKeysFromPositions({
    state,
    kind: effectiveAttackKind(unit),
    positions: attackFrom,
  });
}
